import { Box, Center, Group, Modal, SegmentedControl } from '@mantine/core';
import { DeviceMobileIcon, MonitorIcon } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import type { DeepPartial } from 'react-hook-form';

import { useVenueItemQuery } from '../../../data/venues/useVenueItemQuery.ts';
import { env } from '../../../env.ts';
import type { IncompleteEventFormComplete } from '../../../forms/event/IncompleteEventForm/schema.ts';
import { useEventOptions } from '../../../hooks/useEventOptions/useEventOptions.ts';
import { useI18n } from '../../../hooks/useI18n.ts';
import { PREVIEW_IFRAME_SANDBOX } from '../../../lib/previewIframeSandbox.ts';
import { buildPreviewMessage } from '../../../views/onboarding/lib/buildPreviewMessage.ts';

interface DevicePreviewModalProps {
  opened: boolean;
  onClose: () => void;
  formData: DeepPartial<IncompleteEventFormComplete> | undefined;
}

/**
 * Modal that embeds the gogigi.com /event-preview iframe and streams the
 * current form data into it via postMessage. Used on the onboarding preview
 * step and on the incomplete-event / event edit views to let promoters see
 * how their event will look on the customer site at desktop and mobile sizes
 * without actually publishing it.
 */
export const DevicePreviewModal: FC<DevicePreviewModalProps> = ({ opened, onClose, formData }) => {
  const { t } = useI18n();
  const frontendUrl = env.VITE_FRONTEND_URL;

  const { category, targetAudiences, detail } = useEventOptions();
  const { data: venueData } = useVenueItemQuery(formData?.venue ?? undefined);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [deviceMode, setDeviceMode] = useState<'desktop' | 'mobile'>('desktop');

  const DESKTOP_WIDTH = 1440;
  const DESKTOP_HEIGHT = 900;
  const MOBILE_WIDTH = 390;
  const MOBILE_HEIGHT = 844;
  const VIEWPORT_WIDTH = deviceMode === 'desktop' ? DESKTOP_WIDTH : MOBILE_WIDTH;

  const [scale, setScale] = useState(1);

  const screenRefCallback = (el: HTMLDivElement | null) => {
    if (!el) return;
    const compute = () => setScale(el.clientWidth / VIEWPORT_WIDTH);
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
  };

  // Hand the form view-model over to the iframe via postMessage whenever the
  // underlying data changes. Origin-checked on both sides against
  // VITE_FRONTEND_URL so no other window can talk to the iframe.
  useEffect(() => {
    if (!opened || !frontendUrl || !formData) return;

    const message = buildPreviewMessage(formData, venueData ?? undefined, {
      category: category.data,
      targetAudiences: targetAudiences.data,
      eventDetails: detail.data,
    });
    if (!message) return;

    const post = () => iframeRef.current?.contentWindow?.postMessage(message, frontendUrl);

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== frontendUrl) return;
      if ((event.data as { type?: string } | null)?.type !== 'preview-ready') return;
      post();
    };

    window.addEventListener('message', onMessage);
    post();
    return () => window.removeEventListener('message', onMessage);
  }, [opened, frontendUrl, formData, venueData, category.data, targetAudiences.data, detail.data]);

  if (!frontendUrl) return null;

  return (
    <Modal
      centered
      withCloseButton
      opened={opened}
      size="xl"
      title={
        <SegmentedControl
          data={[
            {
              value: 'desktop',
              label: (
                <Group gap={6} wrap="nowrap">
                  <MonitorIcon size={16} weight="bold" />
                  <span>{t('onboarding_preview_device-desktop') ?? 'Desktop'}</span>
                </Group>
              ),
            },
            {
              value: 'mobile',
              label: (
                <Group gap={6} wrap="nowrap">
                  <DeviceMobileIcon size={16} weight="bold" />
                  <span>{t('onboarding_preview_device-mobile') ?? 'Mobile'}</span>
                </Group>
              ),
            },
          ]}
          size="sm"
          value={deviceMode}
          onChange={(v) => setDeviceMode(v)}
        />
      }
      onClose={onClose}
    >
      <Center style={{ width: '100%', minHeight: 640, padding: '8px 0 16px' }}>
        {deviceMode === 'desktop' ? (
          <Box
            key="desktop"
            maw={1100}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            w="100%"
          >
            <Box
              style={{
                background: '#1f1f1f',
                borderRadius: '14px 14px 4px 4px',
                padding: '22px 16px 16px',
                boxShadow: '0 30px 60px rgba(0,0,0,0.25)',
                position: 'relative',
              }}
              w="100%"
            >
              <Box
                style={{
                  position: 'absolute',
                  top: 9,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: '#444',
                }}
              />
              <div
                ref={screenRefCallback}
                style={{
                  width: '100%',
                  aspectRatio: `${DESKTOP_WIDTH} / ${DESKTOP_HEIGHT}`,
                  background: '#fff',
                  borderRadius: 4,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <iframe
                  ref={iframeRef}
                  sandbox={PREVIEW_IFRAME_SANDBOX}
                  src={`${frontendUrl}/event-preview/`}
                  style={{
                    width: DESKTOP_WIDTH,
                    height: DESKTOP_HEIGHT,
                    border: 0,
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transformOrigin: 'top left',
                    transform: `scale(${scale})`,
                  }}
                  title="Event preview"
                />
              </div>
            </Box>
            <Box
              style={{
                width: 'calc(100% + 60px)',
                maxWidth: '110%',
                height: 14,
                background: 'linear-gradient(180deg, #c0c0c0 0%, #8a8a8a 100%)',
                borderRadius: '0 0 14px 14px',
                position: 'relative',
              }}
            >
              <Box
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 90,
                  height: 5,
                  background: '#6f6f6f',
                  borderRadius: '0 0 6px 6px',
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box
            key="mobile"
            style={{
              width: 'min(340px, 80vw)',
              background: '#1f1f1f',
              borderRadius: 40,
              padding: 14,
              boxShadow: '0 30px 60px rgba(0,0,0,0.25)',
              position: 'relative',
            }}
          >
            <Box
              style={{
                position: 'absolute',
                top: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 80,
                height: 18,
                background: '#000',
                borderRadius: 12,
                zIndex: 1,
              }}
            />
            <div
              ref={screenRefCallback}
              style={{
                width: '100%',
                aspectRatio: `${MOBILE_WIDTH} / ${MOBILE_HEIGHT}`,
                background: '#fff',
                borderRadius: 28,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <iframe
                ref={iframeRef}
                sandbox={PREVIEW_IFRAME_SANDBOX}
                src={`${frontendUrl}/event-preview/`}
                style={{
                  width: MOBILE_WIDTH,
                  height: MOBILE_HEIGHT,
                  border: 0,
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transformOrigin: 'top left',
                  transform: `scale(${scale})`,
                }}
                title="Event preview"
              />
            </div>
          </Box>
        )}
      </Center>
    </Modal>
  );
};
